import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { toast } from 'react-hot-toast';
import { api } from '@/lib/api';
import { Titulo, CreateTituloRequest, UpdateTituloRequest, TituloStatus, Company } from '@/types';

interface TituloModalProps {
  titulo?: Titulo | null;
  isOpen: boolean;
  onClose: () => void;
  companyId?: string;
}

export const TituloModal: React.FC<TituloModalProps> = ({
  titulo,
  isOpen,
  onClose,
  companyId,
}) => {
  const queryClient = useQueryClient();
  const isEditing = !!titulo;

  const [formData, setFormData] = useState<CreateTituloRequest>({
    companyId: companyId || '',
    numero: '',
    valor: 0,
    vencimento: '',
    status: TituloStatus.ABERTO,
    observacoes: '',
    urlBoleto: '',
    email: '',
  });

  // Buscar empresas
  const { data: companies } = useQuery({
    queryKey: ['companies'],
    queryFn: async () => {
      const response = await api.get('/companies?isActive=true');
      return response.data.data.companies as Company[];
    },
  });

  // Preencher formulário quando editar
  useEffect(() => {
    if (titulo) {
      setFormData({
        companyId: titulo.companyId,
        numero: titulo.numero,
        valor: titulo.valor,
        vencimento: titulo.vencimento.split('T')[0], // Converter para formato YYYY-MM-DD
        status: titulo.status,
        observacoes: titulo.observacoes || '',
        urlBoleto: titulo.urlBoleto || '',
        email: titulo.email || '',
      });
    } else {
      // Reset form para criação
      setFormData({
        companyId: companyId || '',
        numero: '',
        valor: 0,
        vencimento: '',
        status: TituloStatus.ABERTO,
        observacoes: '',
        urlBoleto: '',
        email: '',
      });
    }
  }, [titulo, companyId]);

  // Mutação para criar título
  const createMutation = useMutation({
    mutationFn: async (data: CreateTituloRequest) => {
      const response = await api.post('/titulos', data);
      return response.data;
    },
    onSuccess: () => {
      toast.success('Título criado com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['titulos'] });
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao criar título');
    },
  });

  // Mutação para atualizar título
  const updateMutation = useMutation({
    mutationFn: async (data: UpdateTituloRequest) => {
      const response = await api.put(`/titulos/${titulo!.id}`, data);
      return response.data;
    },
    onSuccess: () => {
      toast.success('Título atualizado com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['titulos'] });
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao atualizar título');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isEditing) {
      updateMutation.mutate(formData as UpdateTituloRequest);
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleInputChange = (field: keyof CreateTituloRequest, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? 'Editar Título' : 'Novo Título'}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Empresa */}
        <div>
          <label className="block text-sm font-medium text-foreground text-foreground mb-1">
            Empresa *
          </label>
          <Select
            value={formData.companyId}
            onValueChange={(value) => handleInputChange('companyId', value)}
            disabled={isLoading}
          >
            <option value="">Selecione uma empresa</option>
            {companies?.map((company) => (
              <option key={company.id} value={company.id}>
                {company.name}
              </option>
            ))}
          </Select>
        </div>

        {/* Número */}
        <div>
          <label className="block text-sm font-medium text-foreground text-foreground mb-1">
            Número *
          </label>
          <Input
            type="text"
            value={formData.numero}
            onChange={(e) => handleInputChange('numero', e.target.value)}
            placeholder="Número do título"
            required
            disabled={isLoading}
          />
        </div>

        {/* Valor */}
        <div>
          <label className="block text-sm font-medium text-foreground text-foreground mb-1">
            Valor *
          </label>
          <Input
            type="number"
            step="0.01"
            min="0"
            value={formData.valor}
            onChange={(e) => handleInputChange('valor', parseFloat(e.target.value) || 0)}
            placeholder="0,00"
            required
            disabled={isLoading}
          />
        </div>

        {/* Vencimento */}
        <div>
          <label className="block text-sm font-medium text-foreground text-foreground mb-1">
            Data de Vencimento *
          </label>
          <Input
            type="date"
            value={formData.vencimento}
            onChange={(e) => handleInputChange('vencimento', e.target.value)}
            required
            disabled={isLoading}
          />
        </div>

        {/* Status */}
        <div>
          <label className="block text-sm font-medium text-foreground text-foreground mb-1">
            Status *
          </label>
          <Select
            value={formData.status}
            onValueChange={(value) => handleInputChange('status', value as TituloStatus)}
            disabled={isLoading}
          >
            {Object.values(TituloStatus).map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </Select>
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-medium text-foreground text-foreground mb-1">
            Email
          </label>
          <Input
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            placeholder="email@exemplo.com"
            disabled={isLoading}
          />
        </div>

        {/* URL do Boleto */}
        <div>
          <label className="block text-sm font-medium text-foreground text-foreground mb-1">
            URL do Boleto
          </label>
          <Input
            type="url"
            value={formData.urlBoleto}
            onChange={(e) => handleInputChange('urlBoleto', e.target.value)}
            placeholder="https://boleto.exemplo.com"
            disabled={isLoading}
          />
        </div>

        {/* Observações */}
        <div>
          <label className="block text-sm font-medium text-foreground text-foreground mb-1">
            Observações
          </label>
          <Textarea
            value={formData.observacoes}
            onChange={(e) => handleInputChange('observacoes', e.target.value)}
            placeholder="Observações sobre o título"
            rows={3}
            disabled={isLoading}
          />
        </div>

        {/* Botões */}
        <div className="flex justify-end space-x-2 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={isLoading}
          >
            {isLoading ? 'Salvando...' : isEditing ? 'Atualizar' : 'Criar'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
